import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { OwlCarouselOConfig } from 'ngx-owl-carousel-o/lib/carousel/owl-carousel-o-config';
import { CarouselComponent, SlidesOutputData } from 'ngx-owl-carousel-o';
import { NavigationStart, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  PhotoSwipeItem,
  PhotoSwipeModelRef,
  PhotoSwipeService,
  PhotoSwipeThumbBounds,
} from '../../../../services/image-gallery/photo-swipe.service';
import { ImageGalleryItem } from '../../../../services/catalog/image-helper.service';

export type ProductLayout = 'standard' | 'sidebar' | 'columnar' | 'quickview';

@UntilDestroy()
@Component({
  selector: 'app-image-gallery',
  templateUrl: './image-gallery.component.html',
  styleUrls: ['./image-gallery.component.scss'],
})
export class ImageGalleryComponent implements OnInit, AfterViewInit {
  items!: ImageGalleryItem[];
  currentItem: ImageGalleryItem | null = null;
  galleryRef: PhotoSwipeModelRef | null = null;
  galleryIsClosed = false;
  @Input() productLayout: ProductLayout = 'standard';
  carouselOptions: Partial<OwlCarouselOConfig> = {
    dots: false,
    autoplay: false,
    responsive: {
      0: { items: 1 },
    },
    rtl: false,
  };

  thumbnailsCarouselOptions: Partial<OwlCarouselOConfig> = {
    dots: false,
    autoplay: false,
    margin: 10,
    items: 5,
    responsive: {
      480: { items: 5 },
      380: { items: 4 },
      0: { items: 3 },
    },
    rtl: false,
  };

  @Input() set images(images: ImageGalleryItem[]) {
    this.items = images;
    this.currentItem = this.items.length > 1 ? this.items[1] : this.items[0];
  }

  @HostBinding('class.product-gallery') classProductGallery = true;

  @ViewChild('featuredCarousel', { read: CarouselComponent }) featuredCarousel!: CarouselComponent;

  @ViewChild('thumbnailsCarousel', { read: CarouselComponent }) thumbnailsCarousel!: CarouselComponent;

  @ViewChildren('imageElement', { read: ElementRef }) imageElements!: QueryList<ElementRef>;

  constructor(@Inject(PLATFORM_ID) private platformId: any, private photoSwipe: PhotoSwipeService, private router: Router) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.photoSwipe.load().subscribe();
    }

    this.router.events
      .pipe(
        filter((event: any) => event instanceof NavigationStart),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.galleryIsClosed = true;

        if (this.galleryRef) {
          this.galleryRef.close();
        }
      });
  }

  ngAfterViewInit() {
    if (this.currentItem) this.featuredCarousel.to(this.currentItem.id);
  }

  featuredCarouselTranslated(event: SlidesOutputData): void {
    if (event.slides && event.slides.length) {
      const activeImageId = event.slides[0].id;

      this.currentItem = this.items!.find((x) => x.id === activeImageId) || this.items![0] || null;
    }
  }

  getDirDependentIndex(index: number): number {
    return index;
  }

  onFeaturedImageClick(event: MouseEvent, image: any): void {
    event.preventDefault();
    this.openPhotoSwipe(image);
  }

  onThumbnailImageClick(item: ImageGalleryItem): void {
    this.featuredCarousel.to(item.id);
    this.currentItem = item;
  }

  prevImage() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let index: number = this.items!.indexOf(this.currentItem!);
    if (index === 0) {
      // i would become 0
      index = this.items!.length; // so put it at the other end of the array
    }
    index = index - 1;
    const image: ImageGalleryItem | null = this.items![index];
    this.featuredCarousel.to(image.id);
    this.currentItem = image;
  }

  nextImage() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let index: number = this.items!.indexOf(this.currentItem!) + 1;
    index = index % this.items!.length;
    const image: ImageGalleryItem | null = this.items![index];
    this.featuredCarousel.to(image.id);
    this.currentItem = image;
  }

  openPhotoSwipe(item: ImageGalleryItem | null): void {
    if (!item) {
      return;
    }

    const imageElements = this.imageElements.map((x) => x.nativeElement);
    const images: PhotoSwipeItem[] = this.items.map((eachItem, i) => {
      const tag: HTMLImageElement = imageElements[i];
      const width = (tag.dataset['width'] && parseFloat(tag.dataset['width'])) || tag.naturalWidth;
      const height = (tag.dataset['height'] && parseFloat(tag.dataset['height'])) || tag.naturalHeight;

      return {
        src: eachItem.image.replace('fetch', `fetch/h_${height},w_${width},c_pad`),
        msrc: eachItem.image,
        w: width,
        h: height,
      };
    });

    // noinspection JSUnusedGlobalSymbols
    const options = {
      getThumbBoundsFn: ((index: number) => {
        if (this.galleryIsClosed) {
          return null;
        }

        return this.getThumbBounds(index);
      }) as (index: number) => PhotoSwipeThumbBounds,
      index: this.getDirDependentIndex(this.items!.indexOf(item)),
      bgOpacity: 0.9,
      history: false,
      showAnimationDuration: 0,
    };

    this.photoSwipe.open(images, options).subscribe((galleryRef: any) => {
      galleryRef.listen('beforeChange', () => {
        this.featuredCarousel.to(this.items![this.getDirDependentIndex(galleryRef.getCurrentIndex())].id);
      });

      this.galleryRef = galleryRef;
    });
  }

  getThumbBounds(index: number): PhotoSwipeThumbBounds | null {
    const imageElements = this.imageElements.toArray();
    const dirDependentIndex = this.getDirDependentIndex(index);

    if (!imageElements[dirDependentIndex]) {
      return null;
    }

    const tag = imageElements[dirDependentIndex].nativeElement;
    const width = tag.naturalWidth;
    const height = tag.naturalHeight;
    const rect = tag.getBoundingClientRect();
    const ration = Math.min(rect.width / width, rect.height / height);
    const fitWidth = width * ration;
    const fitHeight = height * ration;

    return {
      x: rect.left + (rect.width - fitWidth) / 2 + window.pageXOffset,
      y: rect.top + (rect.height - fitHeight) / 2 + window.pageYOffset,
      w: fitWidth,
    };
  }
}
